import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ExpandableBrandItem, ExpandableBrandListProps } from './ExpandableBrandList.types'

export type { ExpandableBrandItem } from './ExpandableBrandList.types'

const CHIP_BASE_CLASSES =
  'max-w-[10rem] truncate rounded-xs border border-borderPrimary bg-surface-secondary px-1 py-0.5 text-sm text-gray-700'

const TOGGLE_LABEL_CLASSES = 'underline decoration-dashed underline-offset-2'

const ExpandableBrandList: React.FC<ExpandableBrandListProps> = ({
  brands = [],
  isExpanded = false,
  onExpand = () => {},
  onCollapse = () => {},
  maxVisible = 4,
  className = '',
  chipClassName = '',
  toggleClassName = '',
  collapseLabel = 'View less',
  moreLabelFormatter = (remaining: number) => `View ${remaining} more`,
  expandIcon,
  collapseIcon,
}) => {
  const measureRef = useRef<HTMLDivElement>(null)
  const [firstRowCount, setFirstRowCount] = useState(maxVisible)

  useLayoutEffect(() => {
    const container = measureRef.current
    if (!container || brands.length === 0) return

    const chips = container.querySelectorAll<HTMLElement>('[data-brand-chip]')
    if (chips.length === 0) return

    const firstTop = chips[0].offsetTop
    let count = 0
    for (let i = 0; i < chips.length; i++) {
      if (chips[i].offsetTop !== firstTop) break
      count++
    }
    setFirstRowCount(Math.max(1, count))
  }, [brands])

  const hasOverflow = brands.length > firstRowCount
  const hiddenCount = brands.length - firstRowCount

  const visibleBrands = useMemo(
    () => (isExpanded ? brands : brands.slice(0, firstRowCount)),
    [brands, isExpanded, firstRowCount]
  )

  const containerClasses = useMemo(() => {
    const baseClass = 'flex flex-wrap gap-2'
    return className ? `${baseClass} ${className}` : baseClass
  }, [className])

  const chipClasses = useMemo(
    () => (chipClassName ? `${CHIP_BASE_CLASSES} ${chipClassName}` : CHIP_BASE_CLASSES),
    [chipClassName]
  )

  const toggleClasses = useMemo(() => {
    const baseClass =
      'flex w-full items-center justify-center gap-1 text-xs font-medium text-brand-dark hover:text-gray-900'
    return toggleClassName ? `${baseClass} ${toggleClassName}` : baseClass
  }, [toggleClassName])

  const renderBrandChip = useCallback(
    (brand: ExpandableBrandItem) => (
      <div key={brand.id} className={chipClasses} title={brand.name}>
        {brand.name}
      </div>
    ),
    [chipClasses]
  )

  if (brands.length === 0) return null

  const expandLabel = moreLabelFormatter(hiddenCount)
  const expandToggleIcon = expandIcon ?? <span className="ml-1 inline-block text-[8px]">▼</span>
  const collapseToggleIcon = collapseIcon ?? <span className="ml-1 inline-block text-[8px]">▲</span>

  return (
    <div className="relative">
      <div
        ref={measureRef}
        aria-hidden="true"
        className={`${containerClasses} pointer-events-none invisible absolute inset-x-0 top-0`}
      >
        {brands.map((brand) => (
          <div key={brand.id} data-brand-chip className={chipClasses}>
            {brand.name}
          </div>
        ))}
      </div>

      <div className={containerClasses}>
        {visibleBrands.map(renderBrandChip)}
        {!isExpanded && hasOverflow && (
          <button type="button" onClick={onExpand} className={toggleClasses}>
            <span className={TOGGLE_LABEL_CLASSES}>{expandLabel}</span>
            {expandToggleIcon}
          </button>
        )}
        {isExpanded && hasOverflow && onCollapse && (
          <button type="button" onClick={onCollapse} className={toggleClasses}>
            <span className={TOGGLE_LABEL_CLASSES}>{collapseLabel}</span>
            {collapseToggleIcon}
          </button>
        )}
      </div>
    </div>
  )
}

export default ExpandableBrandList
export { ExpandableBrandList }
